// UK Bait & Lure Guide - Comprehensive data for UK fishing
// Each bait/lure includes: description, which fish it catches, water type, technique tips

export interface Bait {
  id: string;
  name: string;
  type: 'bait' | 'lure';
  category: 'natural' | 'artificial' | 'dead' | 'fly' | 'soft-plastic' | 'hard-lure' | 'spinner' | 'topwater' | 'groundbait' | 'particle';
  water_type: 'freshwater' | 'saltwater' | 'all';
  description: string;
  best_for: string[]; // fish species IDs
  technique: string;
  tips: string;
  pros: string[];
  cons: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  image_urls: string[];
}

export const BAIT_DATA: Bait[] = [
  // ===== NATURAL FRESHWATER BAITS =====
  {
    id: 'worms',
    name: 'Worms (Lobworms)',
    type: 'bait',
    category: 'natural',
    water_type: 'freshwater',
    description: 'The universal freshwater bait. Lobworms are the king of natural baits — their movement and scent trail attract almost every UK freshwater species. Essential for barbel, perch, and tench.',
    best_for: ['barbel', 'perch', 'tench', 'chub', 'roach', 'bream', 'pike', 'grayling', 'brown-trout', 'dace', 'gudgeon', 'flounder'],
    technique: 'Hook through the collar or tail. For barbel, use two or three lobworms on a size 6-8 hook. For perch, a single lobworm on a size 8-10 hook. Thread onto the hair for carp.',
    tips: 'Keep worms in damp newspaper or peat. Warm them before use to increase activity. Lobworms are best in slightly coloured water.',
    pros: ['Universally effective', 'Natural movement attracts fish', 'Cheap and widely available', 'Works year-round'],
    cons: ['Can attract small fish and nuisance species', 'Messy to handle', 'Short shelf life', 'Can fall off the hook easily'],
    difficulty: 'beginner',
    image_urls: [
      'https://upload.wikimedia.org/wikipedia/commons/8/8b/Lumbricus_terrestris.jpg'
    ]
  },
  {
    id: 'maggots',
    name: 'Maggots',
    type: 'bait',
    category: 'natural',
    water_type: 'freshwater',
    description: 'The staple coarse fishing bait. White, red, and green maggots are irresistible to smaller freshwater species. Used by match anglers worldwide. Perfect for building a swim and attracting fish.',
    best_for: ['roach', 'bream', 'dace', 'gudgeon', 'tench', 'perch', 'rudd', 'barbel', 'chub'],
    technique: 'Thread one or two maggots onto a size 12-16 hook. Use red maggots for clearer water, white for murky conditions. Fish on the drop or on the bottom.',
    tips: 'Colour maggot with flavours for extra attraction. Red maggots are more visible in clear water. Combine with casters for a persistent feed.',
    pros: ['Cheap', 'Readily available', 'Great for building a swim', 'Small hookbait avoids small fish'],
    cons: ['Attract small fish', 'Can freeze in winter', 'Short shelf life', 'Some anglers find them unpleasant'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/8/87/Eurobait-maggots.jpg']
  },
  {
    id: 'casters',
    name: 'Casters (Pupae)',
    type: 'bait',
    category: 'natural',
    water_type: 'freshwater',
    description: 'The dormant pupal stage of the maggot. Casters are a superb bait for roach, rudd, and bream. Their hard shell stays on the hook well and they have a subtle, natural appeal.',
    best_for: ['roach', 'rudd', 'bream', 'dace', 'gudgeon', 'tench', 'perch'],
    technique: 'Thread a single caster onto a size 14-18 hook. Use a bait needle to thread onto a hair rig for carp. Fish over a bed of loose maggots.',
    tips: 'Soak casters in warm water before use to make them sink faster. Use as a hookbait over a bed of maggots to catch bigger fish.',
    pros: ['Stays on the hook well', 'Natural colour and scent', 'Great for selective fish', 'Can be flavoured'],
    cons: ['Expensive compared to maggots', 'Less effective in fast water', 'Small size limits target species'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/8/87/Eurobait-maggots.jpg']
  },
  {
    id: 'sweetcorn',
    name: 'Sweetcorn',
    type: 'bait',
    category: 'natural',
    water_type: 'freshwater',
    description: 'One of the most popular and effective baits for carp, bream, and tench. Its bright yellow colour and sweet smell make it highly visible and attractive. A must-have in every coarse angler\'s bag.',
    best_for: ['common-carp', 'tench', 'bream', 'barbel', 'chub', 'roach', 'rudd', 'dace', 'gudgeon'],
    technique: 'Thread 2-3 grains onto a size 10-14 hook. For hair rigging, use a bait stop. Fish on the bottom with a method feeder or simple rig.',
    tips: 'Use tinned sweetcorn for convenience. Glace corn is sweeter and more attractive. Combine with groundbait for a powerful combination.',
    pros: ['Bright and visible', 'Sweet smell attracts fish', 'Cheap and lasts forever', 'Easy to use'],
    cons: ['Attracts nuisance species', 'Can be blown off the hook by small fish', 'Less effective in cold water'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/e/e2/Sweet_corn.jpg']
  },
  {
    id: 'bread',
    name: 'Bread',
    type: 'bait',
    category: 'natural',
    water_type: 'freshwater',
    description: 'The original UK coarse bait. Bread paste, flake, and crust are deadly for chub, carp, and barbel. White bread is a classic bait that has accounted for countless big fish.',
    best_for: ['chub', 'common-carp', 'barbel', 'tench', 'bream', 'roach', 'grayling'],
    technique: 'Bread paste: mould a walnut-sized piece around a size 8-10 hook. Bread flake: squeeze a piece around the hook. Crust: use as a surface bait for chub.',
    tips: 'White bread works best. Remove crust for paste. Squeeze paste firmly around the hook. In cold water, fish bread paste slowly.',
    pros: ['Free and readily available', 'Very effective for chub', 'Easy to mould', 'Good surface bait'],
    cons: ['Washes off quickly in current', 'Attracts small fish', 'Can be difficult to cast', 'Not effective in saltwater'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/f/f3/White_bread_800.jpg']
  },
  {
    id: 'boilies',
    name: 'Boilies',
    type: 'bait',
    category: 'artificial',
    water_type: 'freshwater',
    description: 'The modern carp angler\'s weapon of choice. Boilies are round, boiled baits that come in countless flavours, sizes, and colours. They are designed to avoid small fish and target specimen carp.',
    best_for: ['common-carp', 'barbel', 'tench', 'bream', 'chub'],
    technique: 'Hair rig a single or double boilie on a size 6-10 hook. Use a bait stop to secure. Fish on the bottom with a lead or method feeder.',
    tips: 'Match boilie flavour to the season — fruity in summer, fishy in winter. Use pop-ups for visibility. Match the hatch with size.',
    pros: ['Avoids small fish', 'Endless flavours and options', 'Stays on the hook', 'Long shelf life'],
    cons: ['Expensive', 'Can be slow in cold water', 'Not effective for all species', 'Requires hair rig setup'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/f/f4/Selection_of_Boilies.jpg']
  },
  {
    id: 'pellets',
    name: 'Pellets',
    type: 'bait',
    category: 'artificial',
    water_type: 'freshwater',
    description: 'Oil-rich compressed bait pellets. Used as loose feed and hookbaits. Halibut pellets are the most popular for carp and barbel fishing. They release oils and attractant into the water.',
    best_for: ['common-carp', 'barbel', 'tench', 'bream', 'chub', 'grayling'],
    technique: 'Hook a single 6-12mm pellet through the centre. Use larger pellets as a hair rig. Soak pellets before use to soften them.',
    tips: 'Soak pellets in water for 30-60 minutes. Use matching pellets as loose feed. Fishmeal pellets for barbel, fruit pellets for carp.',
    pros: ['Great loose feed', 'Long shelf life', 'Attracts fish from distance', 'Versatile sizes'],
    cons: ['Need to soak before use', 'Can go soft quickly', 'Not effective in very cold water'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/0/09/Boilies.jpg']
  },
  {
    id: 'particles',
    name: 'Particles (Tiger Nuts, Maples, Brazils)',
    type: 'bait',
    category: 'particle',
    water_type: 'freshwater',
    description: 'Natural food items used as hookbaits and loose feed. Tiger nuts are the most popular, producing a milky sap that carp love. Maples, peanuts, and hemp are also highly effective.',
    best_for: ['common-carp', 'tench', 'bream', 'barbel'],
    technique: 'Thread a tiger nut onto a size 8-10 hook or hair rig. Use a bait stop. For hemp, boil before use and fish loose over the spot.',
    tips: 'Boil tiger nuts for 10 minutes then soak overnight. The milky liquid is a powerful attractant. Use hemp as a particle bed.',
    pros: ['Natural food items', 'Avoids small fish', 'Very effective for carp', 'Long shelf life'],
    cons: ['Require preparation', 'Can be expensive', 'Not effective for all species'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/0/09/Boilies.jpg']
  },
  {
    id: 'hemp',
    name: 'Hemp Seed',
    type: 'bait',
    category: 'particle',
    water_type: 'freshwater',
    description: 'Boiled hemp seed is the ultimate loose feed for coarse fishing. The dark seeds resemble casters and the milky liquid released when boiled is a powerful fish attractant.',
    best_for: ['roach', 'bream', 'tench', 'common-carp', 'dace', 'gudgeon', 'rudd'],
    technique: 'Boil hemp for 10-15 minutes until the seeds split. Drain and use as loose feed. Fish over the hemp bed with a caster or maggot hookbait.',
    tips: 'Add salt or molasses to the cooking water. Fish over a bed of hemp for best results. The split seeds look like natural food.',
    pros: ['Incredibly effective loose feed', 'Cheap in bulk', 'Natural food item', 'Attracts fish from distance'],
    cons: ['Requires cooking', 'Needs to be used fresh', 'Can be messy'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/5/57/Hempseed.jpg']
  },
  {
    id: 'mackerel',
    name: 'Mackerel (Dead Bait)',
    type: 'bait',
    category: 'dead',
    water_type: 'freshwater',
    description: 'The go-to dead bait for pike fishing. Fresh or frozen mackerel fillets or whole sections are irresistible to pike. The oily flesh creates a powerful scent trail.',
    best_for: ['pike', 'conger-eel', 'smooth-hound', 'bass', 'cod', 'pollock'],
    technique: 'Use a whole mackerel fillet or section on a treble hook rig. Fish on the bottom or suspended under a float. Use a wire trace for pike.',
    tips: 'Spike the bait to release oils. Use a pennel rig for whole baits. Dead baits are most effective in cold water when live baits are illegal.',
    pros: ['Highly effective for pike', 'Oily scent trail', 'Available fresh or frozen', 'Legal everywhere'],
    cons: ['Messy to prepare', 'Can attract unwanted predators', 'Needs wire trace for pike'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/c/c3/Scomber_scombrus.jpg']
  },
  {
    id: 'sardines',
    name: 'Sardines (Dead Bait)',
    type: 'bait',
    category: 'dead',
    water_type: 'freshwater',
    description: 'An excellent dead bait for pike and predatory fish. Sardines are oilier than sprats and create a strong scent trail. They are particularly effective in clear, cold water.',
    best_for: ['pike', 'conger-eel', 'smooth-hound', 'bass'],
    technique: 'Use whole or as fillets on a treble hook rig. Float fish or leger on the bottom. Use a wire trace for pike.',
    tips: 'Sardines are oilier than sprats — use them when you need extra scent. Free-thaw them for best results.',
    pros: ['Very oily — strong scent trail', 'Effective in clear water', 'Legal dead bait option', 'Good for big pike'],
    cons: ['Messy', 'Can be expensive', 'Needs wire trace for pike'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/c/c8/Sardina_pilchardus.jpg']
  },
  {
    id: 'smelt',
    name: 'Smelt (Dead Bait)',
    type: 'bait',
    category: 'dead',
    water_type: 'freshwater',
    description: 'A premium dead bait for pike. Smelt have a distinctive silver colour and firm flesh that stays on the hook well. They are particularly effective in very cold water.',
    best_for: ['pike', 'conger-eel'],
    technique: 'Thread a whole smelt on a size 4-6 treble hook. Use a wire trace and fish on the bottom or under a float.',
    tips: 'Smelt are best in very cold water when other dead baits are less effective. Their silver colour is visible in clear water.',
    pros: ['Premium bait for specimen pike', 'Firm flesh stays on hook', 'Effective in cold water', 'Distinctive appearance'],
    cons: ['Expensive', 'Not always available', 'Needs wire trace'],
    difficulty: 'advanced',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/7/73/Sprattus_sprattus.jpg']
  },
  {
    id: 'squid',
    name: 'Squid (Dead Bait)',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'The ultimate sea fishing bait. Squid is highly effective for virtually all UK sea species. It can be used whole, as strips, or as flappers. Its tough skin stays on the hook well.',
    best_for: ['cod', 'bass', 'conger-eel', 'pollock', 'whiting', 'mackerel', 'smooth-hound', 'plaice', 'dab', 'flounder', 'dogfish'],
    technique: 'Use as a whole squid, half squid, or strips. Thread onto a size 2-6 hook. Use a pennel rig for larger baits. Fish on a running ledger or paternoster.',
    tips: 'Squid is tough and stays on the hook. Combine with mackerel for a cocktail bait. Freeze squid in portions for convenience.',
    pros: ['Tough and stays on hook', 'Universal sea bait', 'Long shelf life when frozen', 'Visible in water'],
    cons: ['Can be expensive', 'Needs to be thawed before use', 'Can be slimy'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/d/dd/Loligo_vulgaris.jpg']
  },
  {
    id: 'ragworm',
    name: 'Ragworm',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'The most popular saltwater bait. Ragworm are bloodworm that live in mud and sand. They are highly effective for flatfish, bass, and cod. Their movement and scent are irresistible.',
    best_for: ['plaice', 'dab', 'flounder', 'bass', 'cod', 'whiting', 'pollock', 'smooth-hound'],
    technique: 'Thread onto a size 4-8 hook. Use a single ragworm for smaller fish, multiple for bass and cod. Fish on a paternoster or running ledger.',
    tips: 'Keep ragworm cool and damp. Use the smallest piece that will catch fish. Ragworm are most effective in warm water.',
    pros: ['Universal sea bait', 'Natural movement', 'Highly effective', 'Available from tackle shops'],
    cons: ['Expensive', 'Short shelf life', 'Needs cool storage', 'Can be difficult to hook'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/f/f2/Nereis.jpg']
  },
  {
    id: 'lugworm',
    name: 'Lugworm',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A classic sea fishing bait. Lugworm are larger than ragworm and are excellent for bigger species like cod and bass. They have a strong scent that attracts fish from distance.',
    best_for: ['cod', 'bass', 'conger-eel', 'whiting', 'pollock', 'smooth-hound', 'flounder', 'dab'],
    technique: 'Thread onto a size 2-6 hook. Use whole or as half sections. Fish on a ledger or paternoster rig. Lugworm are best fished on the bottom.',
    tips: 'Black lug is more durable than yellow lug. Use a bait thread to secure on the hook. Lugworm are best in winter for cod.',
    pros: ['Strong scent trail', 'Good for big fish', 'Durable on hook', 'Widely available'],
    cons: ['Messy', 'Expensive', 'Can be difficult to thread on hook', 'Short shelf life'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/7/77/Arenicola_marina.jpg']
  },
  {
    id: 'peeler-crab',
    name: 'Peeler Crab',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'The premium sea bait for bass and smooth-hound. Peeler crab is the soft-shell crab that has recently shed its hard shell. Its sweet, fishy smell is irresistible to sea predators.',
    best_for: ['bass', 'smooth-hound', 'conger-eel', 'cod', 'pollock', 'flounder'],
    technique: 'Peel the crab and use the soft body as bait. Thread onto a size 2-6 hook. Use a pennel rig for larger pieces. Fish on a running ledger.',
    tips: 'Peeler crab is best in spring and autumn. Keep live peelers in a cool, damp container. Use within a few days.',
    pros: ['Premium bait for bass', 'Sweet, attractive smell', 'Highly effective', 'Natural bait'],
    cons: ['Expensive', 'Short shelf life', 'Needs peeling', 'Seasonal availability'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/7/71/Cancer_pagurus.jpg']
  },
  {
    id: 'sand-eel',
    name: 'Sand Eel',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A natural prey item for many sea species. Sand eels are slim, silvery fish that are particularly effective for pollack, bass, and mackerel. They can be used fresh or frozen.',
    best_for: ['pollock', 'bass', 'mackerel', 'cod', 'plaice', 'dab'],
    technique: 'Hook through the eyes or under the chin. Use on a size 4-8 hook. Fish on a spinning rig or ledger. Sand eels are effective when cast and retrieved.',
    tips: 'Fresh sand eels are best. Use a strip of sand eel for flatfish. Sand eels are effective when other baits are not working.',
    pros: ['Natural prey item', 'Highly effective for pollack', 'Good for spinning', 'Available fresh'],
    cons: ['Expensive', 'Short shelf life', 'Needs fresh storage', 'Can be difficult to hook'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/e/e4/Ammodytes_tobianus_(catch).jpg']
  },
  {
    id: 'mussel',
    name: 'Mussel',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A superb natural bait for many sea species. Mussels are bivalve molluscs that are highly effective for cod, bass, and flounder. Their soft flesh and strong scent are irresistible.',
    best_for: ['cod', 'bass', 'flounder', 'plaice', 'dab', 'whiting', 'pollock'],
    technique: 'Shell the mussel and thread onto a size 4-8 hook. Use a bait elastic to secure. Fish on a ledger or paternoster rig.',
    tips: 'Mussels are best in winter for cod. Collect fresh mussels from rocks or buy frozen. Use a bait elastic to keep on hook.',
    pros: ['Natural food item', 'Highly effective for cod', 'Cheap if collected', 'Strong scent'],
    cons: ['Difficult to shell', 'Messy', 'Short shelf life', 'Needs bait elastic'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/2/24/Mytilus_edulis_109693324.jpg']
  },
  // ===== UK SEA FISHING BAITS =====
  {
    id: 'black-lugworm',
    name: 'Black Lugworm',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'The bigger, tougher cousin of the yellow lugworm. Black lugworm (also called "blackies") are the preferred bait for targeting big cod and bass from the shore. Their tough skin stays on the hook through powerful casts.',
    best_for: ['cod', 'bass', 'conger-eel', 'smooth-hound', 'whiting', 'pollock'],
    technique: 'Thread onto a size 1-4 hook using a bait needle. Use a whole black lug for big cod, or half for smaller species. Secure with bait elastic. Fish on a running ledger or pulley rig.',
    tips: 'Black lug are best in winter when cod move inshore. Store in damp seaweed in the fridge. Use within 3-4 days. They can be frozen but lose some potency.',
    pros: ['Tough — stays on hook during big casts', 'Strong scent trail', 'Excellent for big cod', 'Widely available from bait shops'],
    cons: ['More expensive than yellow lug', 'Need to be fresh or freshly frozen', 'Can be hard to source in summer'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/7/77/Arenicola_marina.jpg']
  },
  {
    id: 'yellow-lugworm',
    name: 'Yellow Lugworm',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'The smaller, softer lugworm. Yellow lugworm are cheaper than black lug and are a good all-round sea bait. They have a strong scent but are less durable on the hook.',
    best_for: ['cod', 'whiting', 'dab', 'flounder', 'plaice', 'bass', 'smooth-hound'],
    technique: 'Thread onto a size 4-8 hook. Use a whole yellow lug or half sections. Best used on lighter rigs where big casts aren\'t needed. Fish on a paternoster or simple ledger.',
    tips: 'Yellow lug are great for winter whiting and dab. Combine with ragworm for a cocktail bait. They melt in warm water, so fish quickly.',
    pros: ['Cheaper than black lug', 'Good scent trail', 'Effective for whiting and flatfish', 'Widely available'],
    cons: ['Soft — falls off hook easily', 'Not ideal for big casts', 'Melts in warm water', 'Short shelf life'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/7/77/Arenicola_marina.jpg']
  },
  {
    id: 'king-ragworm',
    name: 'King Ragworm',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'The largest species of ragworm, king ragworm can grow up to 30cm. Their size makes them a superb bait for big bass, cod, and conger eel. They have a powerful scent and active movement.',
    best_for: ['bass', 'cod', 'conger-eel', 'smooth-hound', 'pollock', 'whiting'],
    technique: 'Thread onto a size 1-4 hook. Use the whole worm for big species, or cut into sections for smaller fish. Secure with bait elastic. Fish on a ledger or float rig.',
    tips: 'King rag are best in summer for bass. Store in damp seaweed. Use a bait elastic to keep them on the hook. They can bite — handle carefully.',
    pros: ['Large size — great for big fish', 'Active movement attracts predators', 'Strong scent', 'Excellent for bass'],
    cons: ['Expensive', 'Can bite', 'Short shelf life', 'Not always available'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/f/f2/Nereis.jpg']
  },
  {
    id: 'cockles',
    name: 'Cockles',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A classic flatfish bait. Cockles are small, round shellfish with a sweet, salty taste that flatfish love. They are cheap, easy to use, and highly effective for dab, plaice, and flounder.',
    best_for: ['dab', 'plaice', 'flounder', 'whiting', 'bass', 'smooth-hound'],
    technique: 'Crack the shell and thread the meat onto a size 6-10 hook. Use 2-3 cockles on a hook for a bigger bait. Fish on a paternoster or simple ledger rig.',
    tips: 'Cockles are best in summer for flatfish. Buy frozen cockles from tackle shops — they\'re cheap and ready to use. Combine with ragworm for a cocktail.',
    pros: ['Very cheap', 'Easy to use', 'Excellent for flatfish', 'Sweet scent'],
    cons: ['Small — can fall off hook', 'Not good for big species', 'Need bait elastic'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/0/08/Cerastoderma_edule_01.jpg']
  },
  {
    id: 'razorfish',
    name: 'Razorfish (Razor Clams)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'Long, thin shellfish with a sweet, chewy flesh. Razorfish are a superb bait for bass and flatfish. Their white flesh is tough and stays on the hook well.',
    best_for: ['bass', 'plaice', 'dab', 'flounder', 'whiting', 'pollock'],
    technique: 'Remove from shell and thread onto a size 4-8 hook. Use a whole razorfish or cut into sections. Fish on a ledger or float rig.',
    tips: 'Razorfish are best in summer. Collect fresh from the beach at low tide, or buy frozen from tackle shops. They\'re tough and stay on the hook.',
    pros: ['Tough — stays on hook', 'Sweet scent', 'Good for bass', 'Can be collected free'],
    cons: ['Need to be shelled', 'Not always available', 'Can be expensive to buy'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/3/32/Ensis_directus.jpg']
  },
  {
    id: 'brown-shrimp',
    name: 'Brown Shrimp',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'Small, brown crustaceans found on sandy beaches and mudflats. Brown shrimp are a natural food for bass, flatfish, and many other species. Their movement and scent are irresistible.',
    best_for: ['bass', 'flounder', 'plaice', 'dab', 'whiting', 'smooth-hound'],
    technique: 'Thread onto a size 6-10 hook through the tail. Use a single shrimp or multiple for a bigger bait. Fish on a paternoster or ledger rig.',
    tips: 'Brown shrimp are best in summer. Collect fresh from the beach at low tide, or buy frozen. They\'re especially effective for bass in estuaries.',
    pros: ['Natural food item', 'Highly effective for bass', 'Can be collected free', 'Good for estuary fishing'],
    cons: ['Small', 'Need to be fresh', 'Not always available', 'Can be fiddly to hook'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/8/82/Crangon_crangon.jpg']
  },
  {
    id: 'prawns',
    name: 'Prawns (Crangon)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'Live or frozen prawns are a superb sea bait. Their natural movement and translucent appearance make them highly attractive to bass and flatfish. A premium bait for specimen hunters.',
    best_for: ['bass', 'flounder', 'plaice', 'dab', 'whiting', 'pollock'],
    technique: 'Hook through the tail or head. Use a size 4-8 hook. Fish on a light ledger or float rig. Live prawns are most effective — use a prawn trap to catch them.',
    tips: 'Live prawns are the ultimate bass bait. Keep them alive in a bucket of seawater. Use a simple float rig and fish in estuaries at night.',
    pros: ['Highly effective for bass', 'Natural movement', 'Can be caught fresh', 'Excellent in estuaries'],
    cons: ['Expensive', 'Need to be kept alive', 'Short shelf life', 'Not always available'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/b/b1/Nephrops_norvegicus.jpg']
  },
  {
    id: 'hard-crab',
    name: 'Hard Shell Crab',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'Whole or sectioned hard crab is a devastating bait for smooth-hound and bass. The hard shell provides a crunch that attracts predators, while the internal juices create a powerful scent trail.',
    best_for: ['smooth-hound', 'bass', 'conger-eel', 'cod', 'pollock'],
    technique: 'Use whole small crabs or break larger ones in half. Hook through the shell on a size 1-4 hook. Use a pennel rig for whole crabs. Fish on a running ledger.',
    tips: 'Hard crab is the number one bait for smooth-hound. Collect from rocky shores at low tide. Use fresh — the fresher the better.',
    pros: ['Devastating for smooth-hound', 'Tough — stays on hook', 'Natural bait', 'Can be collected free'],
    cons: ['Messy', 'Need to be fresh', 'Can be hard to hook', 'Spines can hurt'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/1/17/Carcinus_maenas.jpg']
  },
  {
    id: 'mackerel-flapper',
    name: 'Mackerel Flapper',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'A whole mackerel fished as a bait. The flapper is a mackerel with the head and guts removed, leaving the body and tail intact. It\'s a big, oily bait that attracts cod, conger, and smooth-hound.',
    best_for: ['conger-eel', 'smooth-hound', 'cod', 'pollock', 'bass'],
    technique: 'Use a whole mackerel flapper on a size 2/0-4/0 hook. Fish on a heavy ledger or pennel rig. Cast out and leave on the bottom. Use a shock leader for big casts.',
    tips: 'Mackerel flapper is best at night for conger. Use a circle hook for better hook-ups. Fish near rocks and wrecks.',
    pros: ['Big bait — targets big fish', 'Oily scent trail', 'Tough and stays on hook', 'Available fresh or frozen'],
    cons: ['Heavy — needs strong tackle', 'Can attract unwanted species', 'Messy to prepare'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/c/c3/Scomber_scombrus.jpg']
  },
  {
    id: 'squid-mackerel-cocktail',
    name: 'Squid & Mackerel Cocktail',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'The ultimate combo bait. A strip of squid wrapped around a strip of mackerel creates a bait that combines the toughness of squid with the oil of mackerel. Deadly for cod and bass.',
    best_for: ['cod', 'bass', 'conger-eel', 'smooth-hound', 'pollock', 'whiting'],
    technique: 'Cut a strip of squid and a strip of mackerel. Wrap the mackerel around the squid and hook through both. Use a size 2-4 hook. Fish on a running ledger.',
    tips: 'The squid protects the mackerel and keeps it on the hook. Use a bait elastic to secure. This cocktail is lethal for winter cod.',
    pros: ['Best of both worlds', 'Tough and oily', 'Excellent for cod', 'Stays on hook well'],
    cons: ['Need two baits', 'More expensive', 'Requires preparation'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/d/dd/Loligo_vulgaris.jpg']
  },
  {
    id: 'lug-rag-cocktail',
    name: 'Lug & Rag Cocktail',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A classic combo bait that combines the scent of lugworm with the movement of ragworm. This cocktail is a standard for shore anglers targeting cod, bass, and whiting.',
    best_for: ['cod', 'bass', 'whiting', 'dab', 'flounder', 'plaice', 'smooth-hound'],
    technique: 'Thread a lugworm onto the hook, then add a ragworm on top. Use a size 2-6 hook. Secure with bait elastic. Fish on a ledger or paternoster.',
    tips: 'The lug provides scent, the rag provides movement. This combo is greater than the sum of its parts. A must-know bait for winter shore fishing.',
    pros: ['Scent + movement', 'Versatile', 'Effective for many species', 'Classic combo'],
    cons: ['Two baits needed', 'More expensive', 'Can be fiddly to set up'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/f/f2/Nereis.jpg']
  },
  {
    id: 'sandworm',
    name: 'Sandworm (Nereis)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A large, aggressive marine worm found in sandy seabeds. Sandworm are a premium bait for bass and flatfish. Their size and movement make them irresistible to predators.',
    best_for: ['bass', 'flounder', 'plaice', 'dab', 'whiting', 'pollock'],
    technique: 'Thread onto a size 2-6 hook using a bait needle. Use the whole worm for bass, or sections for flatfish. Secure with bait elastic. Fish on a ledger or float rig.',
    tips: 'Sandworm are best in summer for bass. Collect from sandy beaches at low tide, or buy frozen. They\'re tough and stay on the hook.',
    pros: ['Large and active', 'Excellent for bass', 'Tough — stays on hook', 'Can be collected free'],
    cons: ['Expensive to buy', 'Can be hard to find', 'Short shelf life', 'Aggressive — can bite'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/c/c4/Nereis_diversicolor.jpg']
  },
  {
    id: 'whelk',
    name: 'Whelk',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A large sea snail with tough, chewy flesh. Whelk is a traditional bait for cod and conger eel. Its tough texture means it stays on the hook through long soaks.',
    best_for: ['cod', 'conger-eel', 'smooth-hound', 'whiting', 'dogfish'],
    technique: 'Remove from shell and thread onto a size 2-6 hook. Use a whole whelk or cut into sections. Fish on a heavy ledger rig. Leave on the bottom for extended periods.',
    tips: 'Whelk is best in winter for cod. Buy frozen from tackle shops — it\'s cheap and effective. Use a big hook and heavy lead.',
    pros: ['Very tough — stays on hook', 'Cheap', 'Good for big cod', 'Long soak times'],
    cons: ['Not the most attractive bait', 'Needs to be fresh or frozen', 'Can be hard to source'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/5/5d/Buccinum_undatum.jpg']
  },
  {
    id: 'fish-strip-bait',
    name: 'Fish Strips (Mackerel/Sardine)',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'Strips of oily fish used as bait. Mackerel and sardine strips are versatile, cheap, and effective for many sea species. They can be used as hookbaits or cocktail components.',
    best_for: ['cod', 'bass', 'pollock', 'whiting', 'conger-eel', 'smooth-hound', 'dab', 'flounder'],
    technique: 'Cut a mackerel or sardine into strips about 2 inches long. Thread onto a size 2-6 hook. Use a bait elastic to secure. Fish on a ledger or paternoster.',
    tips: 'Mackerel strips are the most versatile sea bait. Use fresh or frozen. Combine with squid for a cocktail. The oil in the fish attracts species from distance.',
    pros: ['Cheap and versatile', 'Oily — strong scent trail', 'Easy to prepare', 'Good for many species'],
    cons: ['Can fall off hook', 'Needs bait elastic', 'Messy', 'Not as effective as whole baits for big fish'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/c/c3/Scomber_scombrus.jpg']
  },
  {
    id: 'sandeel-bait',
    name: 'Sand Eel (Fresh/Frozen)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'Fresh or frozen sand eel is a devastating bait for pollack, bass, and mackerel. Hooked whole, they mimic the natural prey of these predators. Particularly effective when spinning from rocks.',
    best_for: ['pollock', 'bass', 'mackerel', 'cod', 'plaice', 'dab'],
    technique: 'Hook through the eyes or under the chin on a size 4-8 hook. Fish on a spinning rig or ledger. Cast and retrieve to mimic a swimming sand eel.',
    tips: 'Fresh sand eels are best. Use a slow retrieve for bass, faster for pollack. Sand eels are deadly from rock marks and piers.',
    pros: ['Natural prey item', 'Highly effective for pollack', 'Good for spinning', 'Available fresh or frozen'],
    cons: ['Expensive', 'Short shelf life', 'Can be hard to hook', 'Not always available'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/e/e4/Ammodytes_tobianus_(catch).jpg']
  },
  {
    id: 'sprat',
    name: 'Sprat (Dead Bait)',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'Small, oily fish related to herring. Sprat are an excellent dead bait for pike, conger, and cod. Their silver colour and oil content make them highly attractive to predators.',
    best_for: ['pike', 'conger-eel', 'cod', 'smooth-hound', 'pollock'],
    technique: 'Use whole on a treble hook rig or as a strip bait. Thread onto a size 2-6 hook. Fish on a ledger or float rig. Use a wire trace for pike.',
    tips: 'Sprat are best in winter when they shoal inshore. Use fresh or frozen. They\'re cheaper than mackerel and just as effective.',
    pros: ['Oily — strong scent', 'Cheap', 'Good for pike and conger', 'Available frozen'],
    cons: ['Small — needs multiple for big fish', 'Can be messy', 'Not as versatile as mackerel'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/7/73/Sprattus_sprattus.jpg']
  },
  {
    id: 'herring-bait',
    name: 'Herring (Dead Bait)',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'A large, oily fish that makes an excellent dead bait. Herring is particularly effective for pike, conger eel, and large cod. Its oil content creates a powerful scent trail in the water.',
    best_for: ['pike', 'conger-eel', 'cod', 'smooth-hound', 'pollock'],
    technique: 'Use whole or as fillets on a size 2/0-4/0 hook. Fish on a heavy ledger or pennel rig. Use a wire trace for pike. Herring can be cut into sections for smaller species.',
    tips: 'Herring is a premium dead bait for big pike and conger. Use fresh or frozen. The oil in the fish attracts predators from distance.',
    pros: ['Very oily — powerful scent', 'Big bait for big fish', 'Effective for pike and conger', 'Available frozen'],
    cons: ['Expensive', 'Messy', 'Can be hard to source', 'Needs strong tackle'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/1/15/Clupea_harengus_49562304.jpg']
  },
  {
    id: 'garfish-bait',
    name: 'Garfish (Pipefish)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A long, thin fish with a distinctive beak. Garfish are a natural prey item for bass and pollack. Their slim profile and bony structure make them a durable bait.',
    best_for: ['bass', 'pollock', 'mackerel', 'cod'],
    technique: 'Hook through the eyes or body on a size 4-8 hook. Use whole or as sections. Fish on a spinning rig or ledger. Cast and retrieve for bass.',
    tips: 'Garfish are best in summer when they\'re abundant. Collect from piers and harbours, or buy frozen. They\'re tough and stay on the hook.',
    pros: ['Tough — stays on hook', 'Natural prey item', 'Good for bass', 'Can be collected free'],
    cons: ['Not always available', 'Bony — hard to hook', 'Need to be fresh'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/6/6d/Garfish.jpg']
  },
  {
    id: 'black-lug-rag',
    name: 'Black Lug & Rag Cocktail',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'The premium combo bait for big cod. A whole black lugworm tipped with a ragworm creates a bait that combines the toughness and scent of black lug with the movement of rag.',
    best_for: ['cod', 'bass', 'smooth-hound', 'conger-eel', 'whiting'],
    technique: 'Thread a black lugworm onto a size 1-4 hook, then add a ragworm on top. Secure with bait elastic. Fish on a running ledger or pulley rig.',
    tips: 'This is the go-to bait for winter cod fishing. Use a big hook and heavy lead. Fish at night for the best results.',
    pros: ['Premium cod bait', 'Tough and scented', 'Movement from rag', 'Deadly in winter'],
    cons: ['Expensive', 'Need two baits', 'Can be fiddly'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/f/f2/Nereis.jpg']
  },
  {
    id: 'bloodworm-salt',
    name: 'Bloodworm (Saltwater)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'The marine bloodworm is a large, blood-red worm found in muddy seabeds. It\'s a superb bait for flatfish and whiting. Its bright red colour and movement are irresistible.',
    best_for: ['dab', 'plaice', 'flounder', 'whiting', 'bass', 'cod'],
    technique: 'Thread onto a size 6-10 hook. Use a single bloodworm or multiple for a bigger bait. Fish on a paternoster or ledger rig.',
    tips: 'Bloodworm are best in summer for flatfish. Buy frozen from tackle shops — they\'re cheap and effective. Use a bait elastic to keep on hook.',
    pros: ['Bright red — highly visible', 'Natural movement', 'Good for flatfish', 'Cheap'],
    cons: ['Short shelf life', 'Need bait elastic', 'Can be messy', 'Not always available fresh'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/8/82/Nereis_virens.jpg']
  },
  {
    id: 'sandeel-strip',
    name: 'Sand Eel Strip',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'Strips of sand eel used as bait for flatfish and bass. Sand eel strips are tough, oily, and stay on the hook well. They\'re a versatile bait that can be used in many ways.',
    best_for: ['plaice', 'dab', 'flounder', 'bass', 'whiting', 'pollock'],
    technique: 'Cut a sand eel into strips about 2 inches long. Thread onto a size 4-8 hook. Use a bait elastic to secure. Fish on a ledger or paternoster.',
    tips: 'Sand eel strips are great for plaice and dab. Use fresh or frozen. Combine with ragworm for a cocktail bait.',
    pros: ['Tough and oily', 'Good for flatfish', 'Easy to prepare', 'Versatile'],
    cons: ['Need to be fresh or frozen', 'Can be expensive', 'Not as effective as whole sand eel'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/e/e4/Ammodytes_tobianus_(catch).jpg']
  },
  {
    id: 'mussel-bed-bait',
    name: 'Mussel Bed Bait (Mixed)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A mixture of mussels, limpets, and barnacles scraped from rocks. This natural mix is highly effective for wrasse, pollack, and bass. The variety of scents and textures attracts multiple species.',
    best_for: ['pollock', 'bass', 'cod', 'plaice', 'dab', 'flounder', 'whiting'],
    technique: 'Scrape mussels and limpets from rocks at low tide. Use whole or crush into a paste. Hook onto a size 2-6 hook. Fish on a ledger rig near rocky ground.',
    tips: 'Mussel bed bait is free and highly effective. Collect at low tide. Use fresh — the fresher the better. Great for wrasse fishing from rocks.',
    pros: ['Free — collect your own', 'Natural bait', 'Highly effective', 'Variety of scents'],
    cons: ['Need to collect', 'Messy', 'Short shelf life', 'Need to be fresh'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/2/24/Mytilus_edulis_109693324.jpg']
  },
  {
    id: 'frozen-squid-tubes',
    name: 'Frozen Squid Tubes',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'Whole frozen squid tubes are a convenient and effective sea bait. They come pre-cleaned and ready to use. Squid tubes are tough, oily, and stay on the hook through long soaks.',
    best_for: ['cod', 'bass', 'conger-eel', 'smooth-hound', 'pollock', 'whiting', 'dogfish'],
    technique: 'Thaw before use. Cut into strips or use whole on a size 2-4 hook. Use a pennel rig for whole squid. Fish on a running ledger or paternoster.',
    tips: 'Squid tubes are the most convenient sea bait — just thaw and use. They\'re tough, oily, and effective for almost everything. Buy in bulk and freeze.',
    pros: ['Convenient — just thaw and use', 'Tough and stays on hook', 'Long shelf life frozen', 'Universal sea bait'],
    cons: ['Need to thaw before use', 'Can be expensive if not bought in bulk', 'Less effective than fresh squid'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/d/dd/Loligo_vulgaris.jpg']
  },
  {
    id: 'peeler-crab-soft',
    name: 'Peeler Crab (Soft Shell)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'The soft-shell crab after moulting. Soft peeler crab is the ultimate bass bait — its sweet, milky scent drives bass wild. This is a premium bait for specimen hunters.',
    best_for: ['bass', 'smooth-hound', 'flounder', 'plaice', 'dab'],
    technique: 'Peel the soft shell and use the body as bait. Thread onto a size 2-6 hook. Use a pennel rig for larger pieces. Fish on a light ledger or float rig.',
    tips: 'Soft peeler crab is the number one bass bait. Use in spring and autumn when peelers are abundant. Keep live peelers in a cool container.',
    pros: ['Ultimate bass bait', 'Sweet, milky scent', 'Highly effective', 'Natural bait'],
    cons: ['Very expensive', 'Short shelf life', 'Needs peeling', 'Seasonal'],
    difficulty: 'advanced',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/7/71/Cancer_pagurus.jpg']
  },
  {
    id: 'sand-crab',
    name: 'Sand Crab (Hermit Crab)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'Small crabs found in rock pools and on sandy beaches. Sand crab are a natural food for bass and flatfish. Their movement and scent make them a highly effective bait.',
    best_for: ['bass', 'flounder', 'plaice', 'dab', 'whiting'],
    technique: 'Remove from shell and thread onto a size 6-10 hook. Use a single crab or multiple. Fish on a ledger or paternoster rig.',
    tips: 'Sand crab are best in summer for bass and flatfish. Collect from rock pools at low tide. They\'re free and highly effective.',
    pros: ['Free — collect your own', 'Natural bait', 'Effective for bass', 'Movement attracts fish'],
    cons: ['Small', 'Need to collect', 'Short shelf life', 'Not always available'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/1/17/Carcinus_maenas.jpg']
  },
  {
    id: 'worm-tipped-bait',
    name: 'Worm-Tipped Bait',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'A bait combination where a tough bait (squid, fish strip, or crab) is tipped with a piece of ragworm or lugworm. The tough base stays on the hook while the worm adds movement and scent.',
    best_for: ['cod', 'bass', 'smooth-hound', 'whiting', 'dab', 'flounder'],
    technique: 'Thread the base bait onto the hook, then add a small piece of ragworm or lugworm on top. Secure with bait elastic. Fish on a ledger or paternoster.',
    tips: 'This is a versatile and effective technique. Use squid tipped with rag for cod, or fish strip tipped with lug for bass. The combination is greater than the sum of its parts.',
    pros: ['Versatile', 'Tough base + moving tip', 'Effective for many species', 'Good for beginners'],
    cons: ['Two baits needed', 'More expensive', 'Can be fiddly'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/8/8b/Lumbricus_terrestris.jpg']
  },
  {
    id: 'mackerel-feather-bait',
    name: 'Mackerel (Feather Rig)',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'A rig with multiple feathered hooks used to catch mackerel. While not a bait itself, the feather rig is essential for catching live mackerel bait. Mackerel are then used as bait for bigger species.',
    best_for: ['mackerel'],
    technique: 'Cast the feather rig out and retrieve quickly. Mackerel will grab the feathers. Use 3-4 feathers on a rig. Fish from piers, boats, and rocks.',
    tips: 'Feather rigs are the easiest way to catch fresh mackerel bait. Use silver or coloured feathers. Fish in summer when mackerel shoals are near shore.',
    pros: ['Catches fresh bait', 'Easy to use', 'Fun fishing', 'Cheap'],
    cons: ['Only catches mackerel', 'Need to be near mackerel shoals', 'Seasonal'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/c/c3/Scomber_scombrus.jpg']
  },
  {
    id: 'pilchard-bait',
    name: 'Pilchard (Sardine)',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'A large, oily fish similar to sardines. Pilchards are an excellent dead bait for conger, bass, and cod. Their oil content creates a powerful scent trail that attracts predators.',
    best_for: ['conger-eel', 'bass', 'cod', 'smooth-hound', 'pollock'],
    technique: 'Use whole or as fillets on a size 2/0-4/0 hook. Fish on a heavy ledger or pennel rig. Use a wire trace for conger.',
    tips: 'Pilchards are best in autumn when they shoal inshore. Use fresh or frozen. They\'re oilier than mackerel and create a stronger scent trail.',
    pros: ['Very oily — powerful scent', 'Big bait for big fish', 'Effective for conger', 'Available frozen'],
    cons: ['Expensive', 'Messy', 'Can be hard to source', 'Needs strong tackle'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/c/c8/Sardina_pilchardus.jpg']
  },
  {
    id: 'whitebait',
    name: 'Whitebait (Small Fish)',
    type: 'bait',
    category: 'natural',
    water_type: 'saltwater',
    description: 'Small, juvenile fish (sprat, herring, or sand eel larvae). Whitebait are a natural food for bass and pollack. Hooked whole, they\'re a deadly bait for schooling predators.',
    best_for: ['bass', 'pollock', 'mackerel', 'whiting'],
    technique: 'Hook through the eyes or body on a size 6-10 hook. Use whole or as a strip. Fish on a spinning rig or ledger. Cast and retrieve to mimic swimming whitebait.',
    tips: 'Whitebait are best in summer when they\'re abundant. Collect from beaches and estuaries, or buy frozen. They\'re deadly for bass.',
    pros: ['Natural prey item', 'Highly effective for bass', 'Can be collected free', 'Good for spinning'],
    cons: ['Small', 'Need to be fresh', 'Not always available', 'Can be hard to hook'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/1/12/Sprattus_sprattus_England.jpg']
  },
  {
    id: 'bait-frozen-variety',
    name: 'Frozen Bait Mix (Multi-Pack)',
    type: 'bait',
    category: 'dead',
    water_type: 'saltwater',
    description: 'A convenient mix of frozen baits including squid, mackerel, sand eel, and worms. Frozen bait packs are a cost-effective way to stock up for a session. They cover all the basics.',
    best_for: ['cod', 'bass', 'pollock', 'whiting', 'conger-eel', 'smooth-hound', 'dab', 'flounder', 'plaice'],
    technique: 'Thaw baits before use. Cut squid and mackerel into strips. Use worms whole. Mix and match to create cocktail baits.',
    tips: 'Buy frozen bait in bulk for best value. Store in a freezer at home. Thaw only what you need for each session. Frozen bait is just as effective as fresh.',
    pros: ['Cost-effective', 'Variety of baits', 'Long shelf life', 'Convenient'],
    cons: ['Need to thaw before use', 'Less effective than fresh', 'Need freezer space'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/c/c3/Scomber_scombrus.jpg']
  },
  {
    id: 'bloodworm',
    name: 'Bloodworm',
    type: 'bait',
    category: 'natural',
    water_type: 'freshwater',
    description: 'The larva of the non-biting midge. Bloodworm is a highly effective bait for roach, dace, and other small coarse fish. Its red colour and wriggling action are irresistible.',
    best_for: ['roach', 'dace', 'gudgeon', 'bream', 'perch', 'tench'],
    technique: 'Thread a single bloodworm onto a size 16-18 hook. Use a bait needle for larger pieces. Fish on the drop or on the bottom.',
    tips: 'Bloodworm are best in clear water. Use in winter when maggots are less effective. Keep cool and use fresh.',
    pros: ['Highly effective', 'Natural food item', 'Small size avoids small fish', 'Great in winter'],
    cons: ['Expensive', 'Short shelf life', 'Delicate', 'Needs careful handling'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/8/82/Nereis_virens.jpg']
  },
  {
    id: 'cheese-paste',
    name: 'Cheese Paste',
    type: 'bait',
    category: 'artificial',
    water_type: 'freshwater',
    description: 'A classic chub bait. Cheese paste is made from mixing cheese with flour and water. Its strong smell and soft texture make it highly attractive to chub and barbel.',
    best_for: ['chub', 'barbel', 'grayling', 'brown-trout', 'perch'],
    technique: 'Mould a walnut-sized piece around a size 8-10 hook. Fish on the bottom or under a float. Use a simple leger rig.',
    tips: 'Use mature cheddar for strongest smell. Mix with a little flour to stiffen. Fish near overhanging trees where chub patrol.',
    pros: ['Classic chub bait', 'Easy to make', 'Strong smell', 'Effective in clear water'],
    cons: ['Washes off quickly', 'Needs re-baiting often', 'Messy', 'Attracts small fish'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/1/18/Somerset-Cheddar.jpg']
  },
  // ===== FRESHWATER LURES =====
  {
    id: 'spinners',
    name: 'Inline Spinners (Mepps, Blue Fox)',
    type: 'lure',
    category: 'spinner',
    water_type: 'freshwater',
    description: 'The classic spinning lure. Inline spinners have a rotating blade that creates flash and vibration. They are deadly for perch, trout, and chub. Simple to use and highly effective.',
    best_for: ['perch', 'brown-trout', 'rainbow-trout', 'chub', 'grayling', 'pike', 'dace', 'barbel'],
    technique: 'Cast and retrieve at a steady pace. Vary the speed to trigger strikes. Use a slow retrieve in cold water, faster in warm. Fish near structures and weed beds.',
    tips: 'Use lighter spinners in clear water, darker in murky. Silver for bright days, gold for overcast. Match blade size to target species.',
    pros: ['Easy to use', 'Effective for many species', 'Cover lots of water', 'Good visibility'],
    cons: ['Can tangle easily', 'Less effective in very weedy water', 'Can be too aggressive for cautious fish'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/a/ae/A_Rang_Dynamic_18G_spoon_lure_19_gr.jpg']
  },
  {
    id: 'spoons',
    name: 'Spoons',
    type: 'lure',
    category: 'spinner',
    water_type: 'freshwater',
    description: 'A curved, concave lure that wobbles and flashes when retrieved. Spoons are effective for pike, perch, and trout. Their flashing action mimics a wounded baitfish.',
    best_for: ['pike', 'perch', 'brown-trout', 'rainbow-trout', 'chub', 'grayling', 'bass'],
    technique: 'Cast and retrieve with a steady or erratic action. Use a slow wobble for pike, faster for trout. Tip with a strip of mackerel for extra attraction.',
    tips: 'Silver spoons for bright days, gold for overcast. Use larger spoons for pike, smaller for perch. Add a treble hook for better hook-ups.',
    pros: ['Simple and effective', 'Good for covering water', 'Versatile', 'Durable'],
    cons: ['Can tangle on the retrieve', 'Less effective in weedy water', 'Limited action'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/a/ae/A_Rang_Dynamic_18G_spoon_lure_19_gr.jpg']
  },
  {
    id: 'plugs',
    name: 'Hard Plugs (Crankbaits, Minnows)',
    type: 'lure',
    category: 'hard-lure',
    water_type: 'freshwater',
    description: 'Hard-bodied lures that mimic baitfish. Crankbaits dive to specific depths, while minnow plugs swim near the surface. Deadly for pike, perch, and chub.',
    best_for: ['pike', 'perch', 'chub', 'brown-trout', 'rainbow-trout', 'bass'],
    technique: 'Cast and retrieve at varying speeds. Use a stop-start action to trigger strikes. Match the dive depth to where fish are holding.',
    tips: 'Match the lure colour to the conditions. Bright colours in murky water, natural colours in clear. Use a leader for pike.',
    pros: ['Realistic action', 'Cover specific depths', 'Durable', 'Effective for predators'],
    cons: ['Expensive', 'Can be difficult to use', 'Hooks need replacing', 'Can snag in weeds'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/3/34/Angeln_zubehoer_wobbler_01.jpg']
  },
  {
    id: 'soft-plastics',
    name: 'Soft Plastic Lures',
    type: 'lure',
    category: 'soft-plastic',
    water_type: 'freshwater',
    description: 'Flexible, lifelike lures that mimic worms, creatures, and baitfish. Soft plastics are highly effective for perch and bass. Their natural movement triggers strikes.',
    best_for: ['perch', 'bass', 'pike', 'chub', 'brown-trout', 'grayling'],
    technique: 'Thread onto a jig head or Texas rig. Cast and retrieve with a bouncing or swimming action. Use a slow retrieve in cold water.',
    tips: 'Use a jig head that matches the lure size. Colours: bright in murky, natural in clear. Fish near structures and weed beds.',
    pros: ['Lifelike action', 'Versatile', 'Good for finesse fishing', 'Relatively cheap'],
    cons: ['Can tear easily', 'Hooks can come loose', 'Need to be stored properly'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/a/ac/Modern_Fishing_Lure_from_Cleethorpes_%28FindID_521545%29.jpg']
  },
  {
    id: 'jigs',
    name: 'Jig Heads & Micro Jigs',
    type: 'lure',
    category: 'soft-plastic',
    water_type: 'freshwater',
    description: 'Weighted hooks with a lead or tungsten head. Jig heads are used with soft plastics to fish at specific depths. Micro jigs are small, weighted lures that sink fast.',
    best_for: ['perch', 'brown-trout', 'rainbow-trout', 'chub', 'dace', 'grayling'],
    technique: 'Cast and retrieve with a bouncing action. Use a slow hop along the bottom. Fish vertically under a float for deeper water.',
    tips: 'Match the jig head weight to the depth. Use lighter heads in shallow water. Colours: bright for murky, natural for clear.',
    pros: ['Versatile', 'Good for deep water', 'Effective for perch', 'Easy to use'],
    cons: ['Can snag on bottom', 'Need to match with soft plastics', 'Limited action'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/a/ac/Modern_Fishing_Lure_from_Cleethorpes_%28FindID_521545%29.jpg']
  },
  {
    id: 'flies',
    name: 'Flies (Nymphs, Dry Flies, Wet Flies)',
    type: 'lure',
    category: 'fly',
    water_type: 'freshwater',
    description: 'Artificial flies that mimic insects and other natural food items. Fly fishing is the traditional method for trout and grayling. Flies come in thousands of patterns.',
    best_for: ['brown-trout', 'rainbow-trout', 'grayling', 'perch', 'chub'],
    technique: 'Cast with a fly rod and retrieve with a stripping or dead-drift action. Match the fly to the current insect hatch. Use a leader and tippet.',
    tips: 'Match the hatch — use flies that mimic what the fish are eating. Start with a general pattern like a Hare\'s Ear or Pheasant Tail.',
    pros: ['Traditional and satisfying', 'Highly effective for trout', 'Thousands of patterns', 'Artistic and enjoyable'],
    cons: ['Requires special equipment', 'Steep learning curve', 'Wind can be difficult', 'Limited to certain species'],
    difficulty: 'advanced',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/6/63/Durham_Ranger_salmon_fly.jpg']
  },
  {
    id: 'wobblers',
    name: 'Wobblers & Jerkbaits',
    type: 'lure',
    category: 'hard-lure',
    water_type: 'freshwater',
    description: 'Lures that wobble or dart when retrieved. Jerkbaits are worked with sharp rod twitches to mimic a wounded baitfish. Deadly for pike and perch.',
    best_for: ['pike', 'perch', 'chub', 'brown-trout', 'rainbow-trout'],
    technique: 'Cast and retrieve with a stop-start action. Use sharp jerks to trigger strikes. Pause between jerks to let the lure suspend.',
    tips: 'Use a slow retrieve in cold water. Match the lure size to the target species. Use a wire trace for pike.',
    pros: ['Realistic action', 'Good for triggering strikes', 'Effective in clear water', 'Durable'],
    cons: ['Can be expensive', 'Hooks need replacing', 'Can snag in weeds'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/3/34/Angeln_zubehoer_wobbler_01.jpg']
  },
  {
    id: 'poppers',
    name: 'Topwater Poppers',
    type: 'lure',
    category: 'topwater',
    water_type: 'freshwater',
    description: 'Lures that create a splashing, popping action on the surface. Poppers mimic injured baitfish and trigger explosive surface strikes. Best used in calm water.',
    best_for: ['pike', 'perch', 'chub', 'bass'],
    technique: 'Cast and retrieve with sharp rod twitches to create pops and splashes. Pause between pops to let the lure sit. Fish in calm water near structures.',
    tips: 'Use in early morning or late evening when fish are feeding on the surface. Calm water is essential for best results.',
    pros: ['Exciting surface strikes', 'Visual and fun', 'Good for calm water', 'Effective in summer'],
    cons: ['Only works in calm water', 'Limited to surface-feeding fish', 'Can be difficult to use'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/a/ac/Modern_Fishing_Lure_from_Cleethorpes_%28FindID_521545%29.jpg']
  },
  // ===== SALTWATER LURES =====
  {
    id: 'ragworm-lure',
    name: 'Ragworm-Style Soft Plugs',
    type: 'lure',
    category: 'soft-plastic',
    water_type: 'saltwater',
    description: 'Soft plastic lures that mimic ragworm and other marine worms. These are effective for bass and flatfish. Their wriggling action on the retrieve triggers strikes.',
    best_for: ['bass', 'pollock', 'flounder', 'plaice', 'dab'],
    technique: 'Cast and retrieve with a slow, wriggling action. Use a jig head or weighted hook. Fish near the bottom for flatfish.',
    tips: 'Use natural colours in clear water, bright in murky. Fish near estuaries and rock marks where bass feed.',
    pros: ['Effective for bass', 'Lifelike action', 'Good for rough ground', 'Versatile'],
    cons: ['Can be expensive', 'Hooks need replacing', 'Limited to certain species'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/f/f2/Nereis.jpg']
  },
  {
    id: 'metal-jigs',
    name: 'Metal Jigs & Spoons (Sea)',
    type: 'lure',
    category: 'spinner',
    water_type: 'saltwater',
    description: 'Heavy metal lures designed for casting distance and deep water. Metal jigs are effective for pollack, mackerel, and bass. They can be cast long distances from the shore.',
    best_for: ['pollock', 'mackerel', 'bass', 'cod', 'coalfish'],
    technique: 'Cast as far as possible and retrieve with a fast, jerking action. Use a slow retrieve for deeper water. Fish near structures and reef.',
    tips: 'Use lighter jigs in shallow water, heavier in deep. Silver for bright days, dark for overcast. Match the jig weight to the depth.',
    pros: ['Long casting distance', 'Good for deep water', 'Durable', 'Effective for many species'],
    cons: ['Can be heavy to cast', 'Limited action', 'Can snag on bottom'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/a/ae/A_Rang_Dynamic_18G_spoon_lure_19_gr.jpg']
  },
  {
    id: 'surface-lures',
    name: 'Surface Lures (Sea)',
    type: 'lure',
    category: 'topwater',
    water_type: 'saltwater',
    description: 'Lures that fish on or just below the surface. Surface lures are effective for bass and pollack in calm conditions. They create a commotion that attracts predatory fish.',
    best_for: ['bass', 'pollock', 'mackerel'],
    technique: 'Cast and retrieve with a steady or erratic action. Use a slow retrieve for calm water. Fish near the surface in early morning or late evening.',
    tips: 'Best in calm water at dawn or dusk. Use natural colours in clear water. Fish near structures where bass feed.',
    pros: ['Exciting surface strikes', 'Visual and fun', 'Good for calm conditions', 'Effective for bass'],
    cons: ['Only works in calm water', 'Limited to surface feeders', 'Can be difficult to use'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/a/ac/Modern_Fishing_Lure_from_Cleethorpes_%28FindID_521545%29.jpg']
  },
  {
    id: 'sand-eel-lure',
    name: 'Sand Eel Imitation Lures',
    type: 'lure',
    category: 'soft-plastic',
    water_type: 'saltwater',
    description: 'Soft plastic lures that mimic sand eels. These are highly effective for pollack, bass, and mackerel. Their slim profile and swimming action are irresistible to predators.',
    best_for: ['pollock', 'bass', 'mackerel', 'cod'],
    technique: 'Cast and retrieve with a steady swimming action. Use a slow retrieve for deeper water. Fish near sand eel beds.',
    tips: 'Use natural colours in clear water. Fish near the bottom for cod, near the surface for pollack. Match the size to the natural sand eels.',
    pros: ['Highly effective for pollack', 'Lifelike action', 'Good for rough ground', 'Versatile'],
    cons: ['Can be expensive', 'Hooks need replacing', 'Limited to certain species'],
    difficulty: 'intermediate',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/e/e4/Ammodytes_tobianus_(catch).jpg']
  },
  // ===== GROUNDBAIT =====
  {
    id: 'groundbait-mix',
    name: 'Groundbait Mix',
    type: 'bait',
    category: 'groundbait',
    water_type: 'freshwater',
    description: 'A mixture of breadcrumbs, crushed pellets, and attractants that creates a cloud of particles in the water. Groundbait is used to attract fish to your swim and hold them there.',
    best_for: ['roach', 'bream', 'tench', 'dace', 'gudgeon', 'rudd', 'barbel', 'chub'],
    technique: 'Mix groundbait with water to a damp consistency. Form into balls and throw into the swim. Fish over the groundbait bed with a small hookbait.',
    tips: 'Start with small balls and build up. Add casters or maggots to the mix for extra attraction. Fish over the groundbait bed.',
    pros: ['Attracts fish from distance', 'Builds a swim', 'Versatile', 'Cheap'],
    cons: ['Can attract small fish', 'Needs practice to get right', 'Can be over-used'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/5/57/Hempseed.jpg']
  },
  {
    id: 'method-mix',
    name: 'Method Feeder Mix',
    type: 'bait',
    category: 'groundbait',
    water_type: 'freshwater',
    description: 'A stiff groundbait mix designed for use with a method feeder. The mix moulds around the feeder and breaks down slowly, releasing attractants. Perfect for carp and bream.',
    best_for: ['common-carp', 'bream', 'tench', 'barbel'],
    technique: 'Mix method feeder mix with water to a stiff consistency. Mould around a method feeder with a hookbait buried inside. Cast and leave on the bottom.',
    tips: 'Use a method mould for perfect moulding every time. Keep the mix stiff so it holds together during the cast. Fish for 10-15 minutes before recasting.',
    pros: ['Self-contained bait and hookbait', 'Easy to use', 'Effective for carp', 'Good for beginners'],
    cons: ['Only works with method feeders', 'Can be over-used', 'Needs practice'],
    difficulty: 'beginner',
    image_urls: ['https://upload.wikimedia.org/wikipedia/commons/5/57/Hempseed.jpg']
  },
];

// Helper functions
export function searchBaits(query: string): Bait[] {
  const q = query.toLowerCase();
  return BAIT_DATA.filter(bait => 
    bait.name.toLowerCase().includes(q) ||
    bait.description.toLowerCase().includes(q) ||
    bait.technique.toLowerCase().includes(q) ||
    bait.tips.toLowerCase().includes(q)
  );
}

export function filterBaitsByType(type: 'bait' | 'lure'): Bait[] {
  return BAIT_DATA.filter(bait => bait.type === type);
}

export function filterBaitsByCategory(category: string): Bait[] {
  return BAIT_DATA.filter(bait => bait.category === category);
}

export function filterBaitsByWaterType(waterType: string): Bait[] {
  return BAIT_DATA.filter(bait => bait.water_type === waterType || bait.water_type === 'all');
}

export function filterBaitsByDifficulty(difficulty: string): Bait[] {
  return BAIT_DATA.filter(bait => bait.difficulty === difficulty);
}

export function getBaitsForFish(fishId: string): Bait[] {
  return BAIT_DATA.filter(bait => bait.best_for.includes(fishId));
}

export function getBaitById(id: string): Bait | undefined {
  return BAIT_DATA.find(bait => bait.id === id);
}

export function getFishForBait(baitId: string): string[] {
  const bait = BAIT_DATA.find(b => b.id === baitId);
  return bait ? bait.best_for : [];
}

// Categories for UI
export const BAIT_CATEGORIES = [
  { value: 'natural', label: 'Natural Bait' },
  { value: 'artificial', label: 'Artificial Bait' },
  { value: 'dead', label: 'Dead Bait' },
  { value: 'particle', label: 'Particles' },
  { value: 'groundbait', label: 'Groundbait' },
  { value: 'fly', label: 'Flies' },
  { value: 'soft-plastic', label: 'Soft Plastics' },
  { value: 'hard-lure', label: 'Hard Lures' },
  { value: 'spinner', label: 'Spinners & Spoons' },
  { value: 'topwater', label: 'Topwater' },
];
